/**
 * Theme Switcher Component
 *
 * Allows switching between different research-based financial themes
 * for easy comparison and testing
 */

import React, { useState, useEffect } from 'react';
import { Typography } from '@/shared/components/typography/SafeTypography'
import { Button, Select, Space, Card, Divider } from 'antd';

const { Title, Text } = Typography;
const { Option } = Select;

interface ThemeOption {
  value: string;
  label: string;
  description: string;
  colors: {
    background: string;
    primary: string;
    success: string;
  };
}

const themeOptions: ThemeOption[] = [
  {
    value: 'bloomberg',
    label: 'Bloomberg Terminal',
    description: 'Institutional blue with pure black background',
    colors: {
      background: '#0A0A0A',
      primary: '#0066CC',
      success: '#00CC66'
    }
  },
  {
    value: 'tradingview',
    label: 'TradingView',
    description: 'Modern blue-black with bright accents',
    colors: {
      background: '#131722',
      primary: '#2962FF',
      success: '#089981'
    }
  },
  {
    value: 'metatrader',
    label: 'MetaTrader',
    description: 'Professional green with dark blue-gray',
    colors: {
      background: '#0F1419',
      primary: '#00A651',
      success: '#00A651'
    }
  },
  {
    value: 'custom',
    label: 'Custom Research',
    description: 'GitHub-style modern development theme',
    colors: {
      background: '#0D1117',
      primary: '#1F6FEB',
      success: '#238636'
    }
  }
];

export const ThemeSwitcher: React.FC = () => {
  const [currentTheme, setCurrentTheme] = useState<string>('bloomberg'); // Bloomberg Terminal - Industry Gold Standard

  useEffect(() => {
    // Apply theme to document body
    document.body.setAttribute('data-theme-version', currentTheme);

    // Also apply to html element for broader compatibility
    document.documentElement.setAttribute('data-theme-version', currentTheme);
  }, [currentTheme]);

  const handleThemeChange = (theme: string) => {
    setCurrentTheme(theme);
  };

  const getCurrentTheme = () => {
    return themeOptions.find(option => option.value === currentTheme) || themeOptions[0]!;
  };

  return (
    <Card
      title="Research Theme Switcher"
      style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000,
        width: '350px',
        backgroundColor: 'var(--color-card)',
        borderWidth: 0,
        boxShadow: 'var(--shadow-lg)',
        overflow: 'hidden'
      }}
      styles={{
        body: {
          padding: '16px',
          backgroundColor: 'var(--color-card)',
          border: 'none'
        }
      }}
    >
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text strong style={{ color: 'var(--color-text)' }}>Current Theme:</Text>
          <Text style={{ color: 'var(--color-text-secondary)', fontSize: '12px' }}>
            {getCurrentTheme().label}
          </Text>
        </div>

        <Select
          value={currentTheme}
          onChange={handleThemeChange}
          style={{ width: '100%' }}
          placeholder="Select a theme"
          size="small"
        >
          {themeOptions.map(option => (
            <Option key={option.value} value={option.value}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '2px',
                    backgroundColor: option.colors.primary,
                    borderWidth: 0
                  }}
                />
                {option.label}
              </div>
            </Option>
          ))}
        </Select>

        <Divider style={{ margin: '12px 0' }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Text strong style={{ color: 'var(--color-text)' }}>Theme Preview:</Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '2px',
                  backgroundColor: getCurrentTheme().colors.background,
                  borderWidth: 0,
                  flexShrink: 0
                }}
                title="Background"
              />
              <Text style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Background</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '2px',
                  backgroundColor: getCurrentTheme().colors.primary,
                  borderWidth: 0,
                  flexShrink: 0
                }}
                title="Primary"
              />
              <Text style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Primary</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '2px',
                  backgroundColor: getCurrentTheme().colors.success,
                  borderWidth: 0,
                  flexShrink: 0
                }}
                title="Success"
              />
              <Text style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Success</Text>
            </div>
          </div>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <div style={{ padding: '8px 0' }}>
          <Text style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', lineHeight: '1.4' }}>
            {getCurrentTheme().description}
          </Text>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <Button
            size="small"
            onClick={() => handleThemeChange('bloomberg')}
            type={currentTheme === 'bloomberg' ? 'primary' : 'default'}
            style={{ fontSize: '10px', padding: '2px 8px', height: '24px' }}
          >
            Bloomberg
          </Button>
          <Button
            size="small"
            onClick={() => handleThemeChange('tradingview')}
            type={currentTheme === 'tradingview' ? 'primary' : 'default'}
            style={{ fontSize: '10px', padding: '2px 8px', height: '24px' }}
          >
            TradingView
          </Button>
          <Button
            size="small"
            onClick={() => handleThemeChange('metatrader')}
            type={currentTheme === 'metatrader' ? 'primary' : 'default'}
            style={{ fontSize: '10px', padding: '2px 8px', height: '24px' }}
          >
            MetaTrader
          </Button>
          <Button
            size="small"
            onClick={() => handleThemeChange('custom')}
            type={currentTheme === 'custom' ? 'primary' : 'default'}
            style={{ fontSize: '10px', padding: '2px 8px', height: '24px' }}
          >
            Custom
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ThemeSwitcher;
